import React, { ChangeEvent, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import './CustomTable.scss';
import AddNewRequestor from '../../assets/images/tabImages/AddNewRequestor.png';
import nationalR from '../../assets/images/tabImages/map.png';
import eTransferR from '../../assets/images/tabImages/Folder_arrow.png';
import { getComparator } from './AccordionTable'
import { isAPIFetching } from '../utils/appUtils';
import ReactHover, { Hover, Trigger } from '../reactHover/ReactHover';
import DragTableWrapper from '../../components/dataEntryDetails/dataEntryComponents/DragTableWrapper'

interface CustomTableProps {
    setGrid: (value: boolean) => void,
    onTableRowClickHandler?: (row?: any) => void,
    rows: readonly any[],
    headCells: readonly { id: string, label: string, commentField?: boolean }[],
    addNewText?: string,
    defaultSortCol: string,
    defaultSortVal?: string,
    onAddNew?: () => void,
    showAddNew?: boolean,
    apiStatus?: string,
    embedded?: boolean,
    disabled?: boolean,
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number, orderBy: keyof(T), orderVal?: string) {
    const stabilizedThis = array.map((el, index) => ({el, index}));
    stabilizedThis.sort((a, b) => {
        if (orderVal && ((a.el[orderBy] as any ?? "").toString().startsWith(orderVal) != (b.el[orderBy] as any ?? "").toString().startsWith(orderVal))){
            return (a.el[orderBy] as any ?? "").toString().startsWith(orderVal) ? -1 : 1;
        }
        const order = comparator(a.el, b.el);
        if (order !== 0) return order;
        return a.index - b.index;
    });
    return stabilizedThis.map(el => el.el);
}

interface CustomTableHeadProps {
    order: "asc" | "desc",
    orderBy: string,
    onRequestSort: (event: React.MouseEvent, property: string) => void,
    headCells: readonly { id: string, label: string, commentField?: boolean }[],
    includeComment?: boolean,
    children?: readonly JSX.Element[],
}

function CustomTableHead(props: CustomTableHeadProps) {
    const { order, orderBy, onRequestSort, headCells } = props;
    const createSortHandler = (property: string) => (event: React.MouseEvent) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead style={{position: 'sticky', top: 0}}>
            <TableRow>
                {headCells.map((headCell) => (
                    !headCell.commentField || props.includeComment ?
                    <TableCell
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                        style={headCell?.label === 'EMPI' ? { width: '105px' } : { width: 'fit-conetent' }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell> :
                    <></>
                ))}
            </TableRow>
            {props.children}
        </TableHead>
    );
}

export default function CustomTable(props: CustomTableProps) {
    const [order, setOrder] = useState<"asc" | "desc">("asc");
    const [orderVal, setOrderVal] = useState(props.defaultSortVal);
    const [orderBy, setOrderBy] = useState(props.defaultSortCol);
    const [filteredStatus, setFilteredStatus] = useState(false);
    const [filteredRows, setFilteredRows] = useState(props.rows);   // This is shared between both the requestor and contact table!
    const [filterValues, setFilterValues] = useState( { firstName: '', lastName: '', phone: '', fax: '', email: '', } )
    const [requestorFilterValues, setRequestorFilterValues] = useState( { requestorName: "", address: "", city: "", state: "", zip: "" } );
    const [selectedItem, setSelectedItem] = useState<any>(undefined);
    const requestorTable = props.addNewText === 'Add new Requestor';
    const contactTable = props.addNewText === 'Add new Contact';
    const handleRequestSort = (_event: React.MouseEvent, property: string) => {
        setOrderVal(undefined);
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const rowClickHandler = (rowData?: any) => {
        setSelectedItem(rowData);
        props.onTableRowClickHandler && props.onTableRowClickHandler(rowData);
        props.setGrid && props.setGrid(false);
    }

    const optionsCursorTrueWithMargin = {
        followCursor: false,
        shiftX: 0,
        shiftY: 15,
        style: {
            right: "0px",
            zIndex: "9999"
        }
    };

    const filterTable = (e: ChangeEvent<HTMLInputElement>, headCell: string) => {
        setFilterValues({...filterValues, [headCell]: e.target.value})
    }

    const filterRequestorTable = (e: ChangeEvent<HTMLInputElement>, headCell: string) => {
        // Ignore filter changes for comments and en fields.
        if(headCell === "en" || headCell === "comments")
            return;
        setRequestorFilterValues({...requestorFilterValues, [headCell]: e.target.value});
    }

    React.useEffect(() => {
        if(orderBy == "phonefax"){
            if(props.rows.some(r => r.fax.startsWith(orderVal))){
                setOrderBy("fax")
            }else{
                setOrderBy("phone")
            }
        }
    },[orderBy])
    
    // Filtering function for the assign contact table
    React.useEffect(()=>{
        if (contactTable){
            // Don't sort if no data.
            if(props.rows.length === 0)
                return;
            setFilteredStatus(true);
            const filterCategories = Object.keys(filterValues);
            setFilteredRows(
                props.rows.filter((row)=>{
                    return Object.entries(row).every((e)=>{
                        let rowCategory = e[0];
                        let rowValue = e[1] as string;
                        // if the current row is a category that can be filtered...
                        if (filterCategories.includes(rowCategory)) {
                            // and if the row's value includes the value entered into the filter...
                            if (rowValue.toLowerCase().includes(filterValues[rowCategory].toLowerCase())) {
                                return true
                            }
                            else { return false }
                        }
                        // if the current row is not a category that can be filtered, ignore it...
                        else{
                            return true
                        }
                    })
                })
            )
        }
    }, [filterValues])

    // Filtering function for the requestor table
    React.useEffect(() =>{
        if(requestorTable)
        {
            // Don't do anything if there is no data present.
            if(props.rows.length === 0)
                return;

            // Set the filtered flag to show the alternate data set in display().
            setFilteredStatus(true);

            // Write filtered data to the state object
            setFilteredRows(
                props.rows.filter((row)=>{
                    return Object.entries(row).every((e)=>{
                        // This is the category coming from the table
                        const columnFilterCategory : string = e[0] as string;

                        // The data we care about here are requestorName and the address information.
                        // Unfortunately, the address information is delivered as an object bundle instead of single values,
                        // So we have to have different handlers for different column data.
                        switch(columnFilterCategory)
                        {
                            // RequestorName is a direct string compare, simple.
                            case "requestorName":
                            {
                                // Get the current filter value for the requestor name
                                const columnFilterValue : string = requestorFilterValues["requestorName"]?.trim().toLowerCase() ?? "";

                                // If the filter value is blank, no filtering is applied, let all rows through.
                                if(columnFilterValue === "")
                                    return true;

                                // Otherwise, check that the row value contains the filter string
                                let requestorNameValue : string = e[1] as string;
                                requestorNameValue = requestorNameValue.trim().toLowerCase();
                                if(requestorNameValue.includes(columnFilterValue))
                                    return true;
                                // No match detected, don't show the row.
                                return false;
                            }
                            // Address filtering
                            // Address contains address1, address2, city, state, zip in a single object.
                            // We have to break out the individual values and test each column separately.
                            // address2 is ignored.
                            case "requestorAddress":
                            {
                                // Extract row data
                                const addressObj : AddressObj = e[1] as AddressObj;
                                const rowAddr1Val : string = addressObj.address1?.trim().toLowerCase() ?? "";
                                const rowCityVal : string = addressObj.city?.trim().toLowerCase() ?? "";
                                const rowStateVal : string = addressObj.state?.trim().toLowerCase() ?? "";
                                const rowZipVal : string = addressObj.zip?.trim().toLowerCase() ?? "";

                                // Extract filter data
                                const filterAddr1Val : string = requestorFilterValues["address"]?.trim().toLowerCase() ?? "";
                                const filterCityVal : string = requestorFilterValues["city"]?.trim().toLowerCase() ?? "";
                                const filterStateVal : string = requestorFilterValues["state"]?.trim().toLowerCase() ?? "";
                                const filterZipVal : string = requestorFilterValues["zip"]?.trim().toLowerCase() ?? "";

                                // Test each address field. If the filter does not have a value, it is not considered.
                                if(filterAddr1Val !== "" && !rowAddr1Val.includes(filterAddr1Val))
                                    return false;
                                if(filterCityVal !== "" && !rowCityVal.includes(filterCityVal))
                                    return false;
                                if(filterStateVal !== "" && !rowStateVal.includes(filterStateVal))
                                    return false;
                                if(filterZipVal !== "" && !rowZipVal.includes(filterZipVal))
                                    return false;
                                // All filters match, display the row
                                return true;
                            }
                            // Something is invalid or the current column is one we don't care about here, no filter logic applied.
                            default:
                                return true;
                        }
                    })
                })
            )
        }
    }, [requestorFilterValues]);

    type AddressObj = 
    {
        address1 : string,
        address2 : string,
        city : string,
        state : string,
        zip : string
    };

    function display(){
        return (<div className='customTable'>
            {props.disabled && <div className='customTableDisable'></div>}
            <Table size="small" stickyHeader style={requestorTable
                ? { display: 'flex', flexDirection: 'column', width: '100%', position: 'relative', left: 0, margin: 0 }
                : props.addNewText === 'Add new Requestor'
                    ? { bottom: '55px' }
                    : props.addNewText === 'Add new Contact'
                        ? { bottom: '131px', width: '35%' }
                        : { top: '380px', right: '3.2%', overflowY: 'auto' }}>
                <div className="table-row-wrapper" style={{ maxHeight: props.addNewText === 'Add new Requestor' ? '305px' : '235px' }}>
                    <CustomTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        headCells={props.headCells}
                        includeComment={props.rows && props.headCells.some(h => h.commentField && props.rows.some(r => r[h.id]))}
                    >
                        {props.headCells.map((headCell) => { 
                        return (
                            <TableCell key={`tablecell${headCell.id}`} style={{ top: 39, width: 'fit-content' }}>
                                {
                                    requestorTable ? 
                                    <input type="text" onChange={(e) => filterRequestorTable(e, headCell.id)} className='table-filter-input' />
                                    :
                                    <input type="text" onChange={(e) => filterTable(e, headCell.id)} className='table-filter-input' />
                                }
                                
                            </TableCell>
                        )
                    })}
                </CustomTableHead>
                <TableBody>
                    {isAPIFetching(props.apiStatus) ?
                        <TableRow className="row-wrapper no-records" key={'010101'}>
                            <TableCell colSpan={props.headCells && props.headCells.length}>{"Searching..."}</TableCell>
                        </TableRow> : props.rows && !props.rows.length ? <TableRow className="row-wrapper no-records" key={'010101'}>
                            <TableCell colSpan={props.headCells && props.headCells.length}>{"No matching records found"}</TableCell>
                        </TableRow> : stableSort(filteredStatus ? filteredRows : props.rows, getComparator(order, orderBy), orderBy, orderVal)
                            .map((row, index) => {
                                return (
                                    <TableRow style={{ height: "51px" }} className={"row-wrapper" + (row === selectedItem ? " selected" : "")} key={row[index]} onClick={() => rowClickHandler(row)}>
                                        {props.headCells.map((headCell) => {
                                            if (headCell.commentField) {
                                                return (
                                                    props.rows.some(r => r[headCell.id]) ?
                                                        <TableCell>
                                                            {
                                                                row[headCell.id] && row[headCell.id].trim() ?
                                                                    <ReactHover options={optionsCursorTrueWithMargin}>
                                                                        <Trigger type="trigger">
                                                                            <i className="fa fa-comment note-space" aria-hidden="true"></i>
                                                                        </Trigger>
                                                                        <Hover type="hover">
                                                                            <div className="react-hover">
                                                                                <blockquote className="patient-comment">
                                                                                    Comment:<br />
                                                                                    {row[headCell.id]}
                                                                                </blockquote>
                                                                            </div>
                                                                        </Hover>
                                                                    </ReactHover> :
                                                                    <></>
                                                            }
                                                        </TableCell> :
                                                        <></>
                                                )
                                            } else if (headCell.id === 'en') {
                                                return (
                                                    <TableCell align={"left"}>
                                                        {row['eTransfer'] ?
                                                            <Tooltip title="eTransfer">
                                                                <img src={eTransferR} alt="" />
                                                            </Tooltip>
                                                            : ''}
                                                        &nbsp;
                                                        {row['national'] ?
                                                            <Tooltip title="National" >
                                                                <img src={nationalR} alt="" />
                                                            </Tooltip>
                                                            : ''}
                                                    </TableCell>
                                                )
                                            } else if (headCell.id === 'address') {

                                                return (
                                                    <TableCell align={"left"}>
                                                        {row.requestorAddress?.address1}
                                                        <br />
                                                        {row.requestorAddress?.address2}
                                                        {row.requestorAddress?.address2 ? <br /> : ""}
                                                    </TableCell>
                                                );
                                            } else if (headCell.id === 'city' || headCell.id === 'state' || headCell.id === 'zip') {
                                                return (
                                                    <TableCell align={headCell.id === 'state' ? "center" : "left"}>{row.requestorAddress?.[headCell.id]}</TableCell>
                                                )
                                            } else {
                                                return (
                                                    <TableCell align={headCell.id === 'state' ? "center" : "left"}>{row[headCell.id]}</TableCell>
                                                );
                                            }

                                        })}
                                    </TableRow>
                                );
                            }
                            )}
                </TableBody>
            </div>
            {props.showAddNew && <caption className='add-new-note'>
                <a href="#!" onClick={() => {
                    rowClickHandler(undefined)
                    props.onAddNew && props.onAddNew()
                }}>
                    <img
                        src={AddNewRequestor}
                        alt={props.addNewText}
                        className="add-new-icon"
                    />
                    <span className='empty-css'>{props.addNewText}</span>
                </a>
            </caption>}
        </Table></div>)
    }

    return props.embedded ? display() : (
        <>
            <div className='table-backdrop' onClick={() => !requestorTable && props.setGrid(false)}></div>
            <DragTableWrapper requestorTable={requestorTable} setGrid={props.setGrid}>
                {display()}
            </DragTableWrapper>
        </>
    );
}
